import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dices, X } from 'lucide-react';
import apiClient from '../../config/axios';
import ListCard from '../ListCard';
import AttrBadge from './AttrBadge';
import ListCardBody from './ListCardBody';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../utils/analytics';

/**
 * The random-entry draw (issue 164): a dice button in every list page's filter
 * bar, a small dialog (count 1–10, "Only use my current filters"), and a
 * results modal of ListCards with a Reroll button.
 *
 * One component, mounted once — in GenericList — which is what gives it to
 * every Family, built-in or authored, rather than to six hardcoded pages.
 *
 * The draw asks the LIST route with `?random=<N>`: the server reuses the list
 * handler's own filter SQL, so "respect my filters" cannot drift from what the
 * list is showing, and the favorites toggle, `?rulebook=`, search and the
 * declared filters all carry through as `activeParams` — the exact narrowing
 * params the list's own fetch sends, minus page/limit/sort.
 *
 * Results are EPHEMERAL by design: nothing is written to the URL and no state
 * survives the modal, so a draw is not shareable — only the entries it links
 * to are.
 *
 * The modal is PORTALED to document.body because the dice button sits inside
 * the list page's search <form>, and a dialog form nested in a form is invalid
 * HTML. React still bubbles the portal's events through the component tree, so
 * the dialog's submit handler stops propagation or it would fire the outer
 * form's search.
 */

export const RANDOM_MAX = 10;

const clampCount = (raw) => Math.min(Math.max(parseInt(raw, 10) || 1, 1), RANDOM_MAX);

export default function RandomEntry({
  segment, family, activeParams = {}, totalMatching, listUrl, onItemClick,
}) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('setup'); // 'setup' | 'results'
  const [count, setCount] = useState('1');
  const [useFilters, setUseFilters] = useState(true);
  const [rows, setRows] = useState([]);
  const [requested, setRequested] = useState(1);
  const [usedFilters, setUsedFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const buttonRef = useRef(null);
  const modalRef = useRef(null);

  const plural = family.labels.plural;
  const lowerPlural = plural.toLowerCase();
  const hasFilters = Object.keys(activeParams).length > 0;
  const effectiveUseFilters = hasFilters && useFilters;

  // The roll is disabled only when the outcome is KNOWN to be empty: the list's
  // own total answers for the filtered scope, and for the whole family when no
  // filters are active. An unfiltered draw over a filtered list is not knowable
  // from here, so it rolls — and an empty answer shows in the results phase.
  const knownEmpty = totalMatching === 0 && (effectiveUseFilters || !hasFilters);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    if (buttonRef.current) buttonRef.current.focus();
  }, []);

  const roll = useCallback(async (n, withFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        ...(withFilters ? activeParams : {}),
        random: n,
      });
      const response = await apiClient.get(`/api/${segment}?${params}`);
      setRows(response.data[segment] || []);
      setRequested(response.data.random?.requested ?? n);
      setUsedFilters(withFilters);
      setPhase('results');
    } catch (err) {
      console.error(`Error drawing random ${lowerPlural}:`, err);
      setError(`Failed to draw random ${lowerPlural}. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [segment, lowerPlural, activeParams]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // The dice button lives inside the list page's search form; without this,
    // the portal's submit bubbles through the React tree and runs the search.
    e.stopPropagation();
    if (loading || knownEmpty) return;
    const n = clampCount(count);
    setCount(String(n));
    track('random-roll', { page: segment, count: n, filtered: String(effectiveUseFilters) });
    roll(n, effectiveUseFilters);
  };

  const reroll = () => {
    track('random-reroll', { page: segment, count: requested, filtered: String(usedFilters) });
    roll(requested, usedFilters);
  };

  // Focus management: the dialog is a focus trap (Tab cycles inside it, Escape
  // closes, and focus returns to the dice button on close).
  useEffect(() => {
    if (!open) return undefined;
    const node = modalRef.current;
    if (node) {
      const first = node.querySelector('input, select, textarea, button:not([data-dialog-close])')
        || node.querySelector('button, a[href]');
      (first || node).focus();
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(modalRef.current.querySelectorAll(
        'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )).filter((el) => !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last
        || !modalRef.current.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, phase, close]);

  const card = family.list.card;
  const printed = rows.some((row) => row.rulebook_name !== undefined);
  const short = phase === 'results' && !loading && rows.length > 0 && rows.length < requested;

  const modal = open ? createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label={`Random ${lowerPlural}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`card w-full ${phase === 'results' ? 'max-w-2xl' : 'max-w-md'} max-h-[calc(100dvh-2rem)] overflow-y-auto relative`}
      >
        <button
          type="button"
          data-dialog-close
          onClick={close}
          className="absolute top-3 right-3 p-1"
          aria-label="Close"
        >
          <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
        </button>

        {phase === 'setup' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="section-header pr-8">Random {lowerPlural}</h2>

            <div>
              <label
                htmlFor="random-count"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                How many?
              </label>
              <input
                id="random-count"
                type="number"
                min={1}
                max={RANDOM_MAX}
                className="input w-full"
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </div>

            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={effectiveUseFilters}
                disabled={!hasFilters}
                onChange={(e) => setUseFilters(e.target.checked)}
              />
              <span>
                Only use my current filters
                {!hasFilters && <span style={{ color: 'var(--text-muted)' }}> (no filters active)</span>}
              </span>
            </label>

            {knownEmpty && (
              <p className="text-sm" role="status" style={{ color: 'var(--text-muted)' }}>
                No {lowerPlural} match.
              </p>
            )}
            {error && <p className="error-message text-sm">{error}</p>}

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={close} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || knownEmpty}>
                {loading ? 'Rolling…' : 'Roll'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <h2 className="section-header pr-8">Random {lowerPlural}</h2>

            {loading ? (
              <div className="flex justify-center items-center h-32" role="status" aria-live="polite">
                <div className="loading-spinner" role="status" aria-label="Loading"></div>
              </div>
            ) : (
              <>
                {rows.length === 0 ? (
                  <p className="no-results" role="status">
                    No {lowerPlural} match{usedFilters ? ' your filters' : ''}.
                  </p>
                ) : (
                  <>
                    {short && (
                      <p className="text-sm" role="status" style={{ color: 'var(--text-muted)' }}>
                        Only {rows.length} match{rows.length === 1 ? 'es' : ''}
                        {usedFilters ? ' your filters' : ''}.
                      </p>
                    )}
                    <div className="space-y-4" aria-label={`Random ${family.labels.singular.toLowerCase()} results`}>
                      {rows.map((row) => (
                        <ListCard
                          key={row.slug}
                          to={`/${segment}?slug=${row.slug}`}
                          state={{ from: listUrl }}
                          onClick={() => onItemClick && onItemClick(row)}
                          title={row[card.title]}
                          badges={card.badges.length ? (
                            <>
                              {card.badges.map((attr) => (
                                <AttrBadge key={attr.key} attr={attr} value={row[attr.key]} />
                              ))}
                            </>
                          ) : null}
                          rulebookName={printed ? (row.rulebook_name || '') : undefined}
                          showFavorite={isAuthenticated && family.favoritable}
                          entityType={segment}
                          slug={row.slug}
                        >
                          <ListCardBody blocks={card.blocks} row={row} />
                        </ListCard>
                      ))}
                    </div>
                  </>
                )}
                {error && <p className="error-message text-sm">{error}</p>}
              </>
            )}

            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={() => setPhase('setup')} className="back-link">
                Change settings
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={close} className="btn btn-secondary">
                  Close
                </button>
                <button
                  type="button"
                  onClick={reroll}
                  className="btn btn-primary"
                  disabled={loading || rows.length === 0}
                >
                  <Dices className="w-4 h-4 inline-block mr-1" aria-hidden="true" />
                  Reroll
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => {
          track('random-open', { page: segment });
          setPhase('setup');
          setOpen(true);
        }}
        className="btn btn-secondary"
        aria-label={`Random ${lowerPlural}`}
        title={`Random ${lowerPlural}`}
      >
        <Dices className="w-5 h-5" aria-hidden="true" />
      </button>
      {modal}
    </>
  );
}
